/**
 * The attribute catalogue (brief §7).
 *
 * Two kinds, and the distinction is load-bearing rather than cosmetic:
 *
 *   `stars` — 0 to 5, more is better. Feeds `quality(s)` and therefore the GDI.
 *   `scale` — 0 to 5 as a position between two named ends. Purely descriptive, GDI-neutral.
 *
 * Age and build are scales because a star rating asserts a ranking of bodies that nobody
 * meant, and because "show me cells where the crowd is young" is a filter that stars cannot
 * express. Same storage, different meaning, different control in the UI.
 */

export type AttributeKind = 'stars' | 'scale';

export type Attribute = {
  key: string;
  label: string;
  emoji: string;
  kind: AttributeKind;
  order: number;
  /** `scale` only: the labels at 0 and at 5. */
  ends?: readonly [string, string];
};

export const MAX_STARS = 5;

const KEY_SHAPE = /^[a-z][a-z0-9_]{1,23}$/;

export const DEFAULT_ATTRIBUTES: readonly Attribute[] = [
  { key: 'nett', label: 'Nett', emoji: '😊', kind: 'stars', order: 10 },
  { key: 'huebsch', label: 'Hübsch', emoji: '✨', kind: 'stars', order: 20 },
  { key: 'stylisch', label: 'Stylisch', emoji: '🕶️', kind: 'stars', order: 30 },
  { key: 'sportlich', label: 'Sportlich', emoji: '🏃', kind: 'stars', order: 40 },
  { key: 'lustig', label: 'Lustig', emoji: '😄', kind: 'stars', order: 50 },
  { key: 'gepflegt', label: 'Gepflegt', emoji: '💇', kind: 'stars', order: 60 },
  { key: 'interessant', label: 'Interessant', emoji: '🧠', kind: 'stars', order: 70 },
  { key: 'intelligenz', label: 'Intelligenz', emoji: '🎓', kind: 'stars', order: 80 },
  {
    key: 'alter',
    label: 'Alter',
    emoji: '🎂',
    kind: 'scale',
    order: 90,
    ends: ['sehr jung', 'sehr alt'],
  },
  {
    // Not `gewicht`: what is meant is the impression a crowd gives, not a number of kilos,
    // and the key should say so. Labels are configuration; the key is for ever.
    key: 'statur',
    label: 'Statur',
    emoji: '⚖️',
    kind: 'scale',
    order: 100,
    ends: ['sehr schlank', 'sehr kräftig'],
  },
];

export type CatalogueProblem = { key: string; problem: string };

/**
 * Validate a catalogue from configuration.
 *
 * Returns problems rather than throwing so the caller can report all of them at once — a
 * config file with three mistakes should not need three restarts to find them.
 */
export function validateCatalogue(attributes: readonly Attribute[]): CatalogueProblem[] {
  const problems: CatalogueProblem[] = [];
  const seen = new Set<string>();

  for (const attribute of attributes) {
    if (!KEY_SHAPE.test(attribute.key)) {
      problems.push({ key: attribute.key, problem: 'key must match ^[a-z][a-z0-9_]{1,23}$' });
    }
    if (seen.has(attribute.key)) {
      problems.push({ key: attribute.key, problem: 'duplicate key' });
    }
    seen.add(attribute.key);

    if (attribute.kind !== 'stars' && attribute.kind !== 'scale') {
      problems.push({ key: attribute.key, problem: 'kind must be stars or scale' });
    }
    if (attribute.kind === 'scale' && (attribute.ends?.length ?? 0) !== 2) {
      problems.push({ key: attribute.key, problem: 'a scale attribute needs two end labels' });
    }
    if (attribute.label.trim() === '') {
      problems.push({ key: attribute.key, problem: 'label must not be empty' });
    }
  }
  return problems;
}

export function sortedCatalogue(attributes: readonly Attribute[]): Attribute[] {
  return [...attributes].sort((a, b) => a.order - b.order || (a.key < b.key ? -1 : 1));
}

export type Ratings = Record<string, number>;

export type RatingsCheck = { ok: true; ratings: Ratings } | { ok: false; problem: string };

/**
 * Validate what a client submitted against the catalogue.
 *
 * The rule worth stating out loud: **at least one `stars` attribute is required.** A report
 * carrying only scales describes a crowd's age and build and says nothing about whether
 * anything was going on — it would contribute a quality of nothing to the density it is
 * supposed to be reporting.
 */
export function checkRatings(input: unknown, catalogue: readonly Attribute[]): RatingsCheck {
  if (input === null || typeof input !== 'object' || Array.isArray(input)) {
    return { ok: false, problem: 'ratings must be an object' };
  }

  const known = new Map(catalogue.map((attribute) => [attribute.key, attribute]));
  const ratings: Ratings = {};
  let starCount = 0;

  for (const [key, value] of Object.entries(input as Record<string, unknown>)) {
    const attribute = known.get(key);
    if (attribute === undefined) return { ok: false, problem: `unknown attribute ${key}` };
    if (typeof value !== 'number' || !Number.isInteger(value) || value < 0 || value > MAX_STARS) {
      return { ok: false, problem: `${key} must be a whole number from 0 to ${MAX_STARS}` };
    }
    ratings[key] = value;
    if (attribute.kind === 'stars') starCount += 1;
  }

  if (starCount === 0) {
    return { ok: false, problem: 'at least one star attribute must be rated' };
  }
  return { ok: true, ratings };
}

/**
 * `quality(s)` from brief §8: the mean of the rated **star** attributes, scaled to 0..1.
 *
 * Scale attributes are excluded, and that exclusion is the single most important line in this
 * file. Counting age as a star would make an older crowd score lower for being older.
 */
export function quality(ratings: Ratings, catalogue: readonly Attribute[]): number {
  const stars = new Set(
    catalogue.filter((attribute) => attribute.kind === 'stars').map((attribute) => attribute.key),
  );

  let total = 0;
  let count = 0;
  for (const [key, value] of Object.entries(ratings)) {
    if (!stars.has(key)) continue;
    total += value;
    count += 1;
  }
  // The caller guarantees at least one star rating; returning 0 rather than NaN keeps a
  // malformed row from poisoning an aggregate if that ever stops being true.
  return count === 0 ? 0 : total / count / MAX_STARS;
}

/** The mean of each rated `scale` attribute, for a cell's facets. */
export function facets(ratings: Ratings, catalogue: readonly Attribute[]): Record<string, number> {
  const scales = new Set(
    catalogue.filter((attribute) => attribute.kind === 'scale').map((attribute) => attribute.key),
  );
  const out: Record<string, number> = {};
  for (const [key, value] of Object.entries(ratings)) {
    if (scales.has(key)) out[key] = value;
  }
  return out;
}

export const CROWD_SIZES = { single: 1, few: 2, many: 3 } as const;
export type Crowd = (typeof CROWD_SIZES)[keyof typeof CROWD_SIZES];

export function isCrowd(value: unknown): value is Crowd {
  return value === 1 || value === 2 || value === 3;
}

export const VISIBILITY = { private: 0, friends: 1, everyone: 2 } as const;
export type Visibility = (typeof VISIBILITY)[keyof typeof VISIBILITY];

export function isVisibility(value: unknown): value is Visibility {
  return value === 0 || value === 1 || value === 2;
}
